import { useQuery } from '@tanstack/react-query'
import { AdminService } from '@app/services/adminService'
import { useEffect } from 'react'
import { useSetAtom } from 'jotai'
import { clientNameAtom } from '@app/state/clientsAtom'
import { isAuthenticatedAtom, isLoadingAuthStateAtom } from '@app/state/authAtom'
import { Client, UseAuthResponse } from '@app/types'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { getCurrentSubdomain } from '@app/utils/getCurrentSubdomain'
import AuthService from '@app/services/authService'
import { BASE_API_URL } from '@app/app-constants'
import { useAvailableClients } from '@app/hooks/useAvailableClients'
import loginRedirect from '@app/utils/loginRedirect'

export const useAuth = (): UseAuthResponse => {
  const setSelectedClient = useSetAtom(clientNameAtom)
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom)
  const setIsLoadingAuth = useSetAtom(isLoadingAuthStateAtom)
  const { fetchFeatureFlagsForClient } = useFeatureFlags()
  // Will be called once client name is set
  useAvailableClients()

  const currentSubdomain = getCurrentSubdomain(
    window.location.hostname,
    import.meta.env.VITE_SAFE_DOMAIN ?? 'rex.local',
  )

  const initiateLogin = () => {
    setIsLoadingAuth(true)
    loginRedirect()
  }
  const initiateLogout = () => {
    setIsLoadingAuth(true)
    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('client_name')
    localStorage.removeItem('temp_user_role')
    window.location.href = `${BASE_API_URL}/auth/logout`
  }

  const {
    data,
    isSuccess,
    isError,
    refetch: fetchPostLoginUserProfile,
  } = useQuery({
    queryKey: ['post-login-user-profile'],
    queryFn: AdminService.getPostLoginUserProfile,
    enabled: false,
  })

  useEffect(() => {
    if (isSuccess && data) {
      if (currentSubdomain) {
        const matchedClient = data.data.available_clients.find(
          (client: Client) => client.subdomain === currentSubdomain,
        )
        if (matchedClient) {
          localStorage.setItem('client_name', matchedClient.subdomain)
          setSelectedClient(matchedClient.subdomain)
        } else {
          localStorage.setItem('client_name', data.data.defaultClient)
          setSelectedClient(data.data.defaultClient)
        }
      } else {
        localStorage.setItem('client_name', data.data.defaultClient)
        setSelectedClient(data.data.defaultClient)
      }
      // We don't really need to wait for this...
      AuthService.incrementLoginCounter(data.data.user.user_uuid)

      setIsAuthenticated(true)
      localStorage.setItem('isAuthenticated', 'true')

      setIsLoadingAuth(false)
    }
  }, [
    isSuccess,
    data,
    setSelectedClient,
    fetchFeatureFlagsForClient,
    setIsAuthenticated,
    setIsLoadingAuth,
    currentSubdomain,
  ])

  useEffect(() => {
    if (isError) {
      setIsLoadingAuth(false)
      loginRedirect(window.location.pathname)
    }
  }, [isError, setIsLoadingAuth])

  return { fetchPostLoginUserProfile, initiateLogin, initiateLogout }
}
